"use client";

import React, { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const modalities = [{
  title: "Chiropractic Adjustments",
  description: "Precision care for your spine and nervous system. At Bodywerks, we restore natural movement and alleviate pain through expert manual adjustments tailored to the unique needs of our Cyprus community, from Limassol to Nicosia.",
  benefits: ["Restoration of spinal mobility and biomechanical function", "Reduction of nervous system interference and stress", "Long-term relief from chronic back, neck, and joint pain"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1609030/img/chiropractic_adjustment_bodywerks.png"
}, {
  title: "Acupuncture & Dry Needling",
  description: "A clinical approach to ancient healing. Our practitioners use fine needles to stimulate specific points, releasing deep muscle tension, reducing inflammation, and promoting the body's natural recovery processes.",
  benefits: ["Targeted trigger point release for chronic muscle knots", "Significant reduction in systemic inflammation and pain", "Enhanced blood flow and neurological recovery support"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1609030/img/acupuncture_needling_limassol.png"
}, {
  title: "Therapeutic Massage",
  description: "Focused soft-tissue therapy designed for recovery and vitality. Whether you're an athlete or managing daily stress, our massage specialists help reduce tension and perfectly complement your clinical chiropractic care.",
  benefits: ["Deep tissue tension relief and improved flexibility", "Reduction in cortisol levels and mental stress", "Improved lymphatic drainage and localized circulation"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1609030/img/massage_therapy_nicosia.png"
}, {
  title: "Pregnancy Chiropractic",
  description: "Gentle, safe, and effective care for expectant mothers. We focus on pelvic alignment and nervous system health to ensure a more comfortable pregnancy and support a smoother birth process for you and your baby.",
  benefits: ["Relief from pregnancy-induced pelvic and sciatic pain", "Optimized positioning for maternal comfort and birth", "Specialized Webster technique certified practitioner care"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1609030/img/pregnancy_care_cyprus.png"
}];
export default function ModalitiesOfCare() {
  return <section data-section-id="80194" id="specialized-treatments" className="w-full flex flex-col bg-background text-foreground">
      {modalities.map((modality, index) => {
      const isEven = index % 2 === 0;
      return <ModalityRow key={modality.title} modality={modality} index={index} isEven={isEven} />;
    })}
    </section>;
}
function ModalityRow({
  modality,
  index,
  isEven
}: {
  modality: (typeof modalities)[0];
  index: number;
  isEven: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  return <div data-index={index} className={`py-24 md:py-32 transition-colors duration-500 ${isEven ? "bg-background" : "bg-muted/30"}`}>
      <div ref={ref} className={`container mx-auto px-6 max-w-6xl transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          {}
          <div className={`order-1 ${isEven ? "md:order-1" : "md:order-2"} w-full`}>
            <div className="border border-border p-[12px] bg-background shadow-lg rounded-xl overflow-hidden">
              <img src={modality.image} alt={modality.title} className="w-full aspect-[4/3] object-cover rounded-sm" />
            </div>
          </div>

          {}
          <div className={`order-2 ${isEven ? "md:order-2" : "md:order-1"} flex flex-col justify-center`}>
            <h3 className="font-serif text-3xl md:text-5xl text-primary mb-6 tracking-tight">
              {modality.title}
            </h3>
            <p className="font-sans text-secondary mb-8 leading-relaxed text-[16px] md:text-[18px]">
              {modality.description}
            </p>
            <ul className="space-y-4 mb-10">
              {modality.benefits.map((benefit, j) => <li key={j} data-index={j} className="flex items-start gap-3 font-sans text-secondary text-[15px] md:text-[16px]">
                  <Check className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <span>{benefit}</span>
                </li>)}
            </ul>
            <div>
              <Button variant="outline" className="font-sans rounded-full px-10 py-7 text-[16px] border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-all duration-300 shadow-sm hover:shadow-md">
                Learn More About {modality.title}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}